import { buildLoyaltyCommitPayload } from '@/api/loyalty-redeem';
import { WizardShell } from '@/components/layout/WizardShell';
import { Button } from '@/components/ui';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from '@kgc/i18n';
import { WizardPanel, handleWizardPanelKeyNav } from '@kgc/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { StepWizardShell } from './components/StepWizardShell';
import { StickySummaryFooter } from './components/StickySummaryFooter';
import { WizardSubHeader } from './components/WizardSubHeader';
import { useRentalCheckout } from './hooks/useDeposits';
import {
  CostBearerStep,
  DetailsStep,
  EquipmentPackageStepV2,
  EquipmentStep,
  PackageAndAccessoriesStep,
  PartnerStep,
  SummaryStep,
} from './steps';
import { PENDING_ONE_TIME_SENTINEL, useWizardStore } from './wizard-store';

/**
 * Rental wizard panel ids — stable keys used both as React list keys and as
 * the `id` prop passed to shared `WizardPanel` (DOM ids become `rental-panel-${id}`).
 */
export type PanelId = 'partner' | 'equipment' | 'packages' | 'costBearer' | 'schedule' | 'payment';

/** Merge HH:MM local time into Date, output as ISO (UTC). User enters local time. */
function mergeDateAndTime(date: Date, time: string | null): string {
  if (!time) return date.toISOString();
  const [hours, minutes] = time.split(':').map(Number);
  if (hours === undefined || minutes === undefined) return date.toISOString();
  const merged = new Date(date);
  // CR-F1: Use setHours (local), not setUTCHours — user enters local time (CET/CEST)
  merged.setHours(hours, minutes, 0, 0);
  return merged.toISOString();
}

/**
 * Rental Wizard — Multi-Panel Order Builder (ADR-103 / Epic E-105)
 *
 * Non-sequential 5-panel accordion, nem linear wizard. A pultos bármelyik
 * panelt bármikor nyitja/módosítja; `pricing` reaktívan újrakalkulál minden
 * mutáció után (recalcPricing, wizard-store-ban).
 *
 * Panelok (Sally fizikai pulti sorrend):
 *   1. Partner  2. Bérgép+Tartozék  3. Cost bearer  4. Időszak  5. Fizetés
 *
 * NOTE: Aláírás POST-CREATION — a wizard DRAFT rental-t hoz létre, aláírás
 * a RentalDetailPage → Contract tabon (ContractService.signContract).
 */
export function RentalWizardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  // 4-lens HOTFIX 2026-04-13 H1 (INP regresszió):
  // Was: full-store destructure → every store mutation re-renders RentalWizardPage + 5 panel trees.
  // FIX: useShallow selector — only re-render when the actually-read fields change (shallow compare).
  // INP improvement on panel toggle / notes typing scenarios.
  const {
    openPanels,
    togglePanel,
    collapseAllPanels,
    expandInvalidOnly,
    reset,
    partner,
    costBearerId,
    grantId,
    pendingOneTimeGrant,
    setGrantId,
    setPendingOneTimeGrant,
    cart,
    startDate,
    endDate,
    startTime,
    endTime,
    useHourlyBilling,
    pricing,
    paymentMethod,
    depositPaid,
    notes,
    crossSellItems,
    selectedPackage,
    packageClassifiedItems,
    consignmentQuantities,
    accessoryPackage,
    accessoryQuantities,
    packageSelections,
    migrateAccessoryPackageToSelections,
    overrideExpiredId,
    overrideReason,
    loyaltyRedeem,
    draftId,
    getPanelValidity,
    isSubmittable,
  } = useWizardStore(
    useShallow(s => ({
      openPanels: s.openPanels,
      togglePanel: s.togglePanel,
      collapseAllPanels: s.collapseAllPanels,
      expandInvalidOnly: s.expandInvalidOnly,
      reset: s.reset,
      partner: s.partner,
      costBearerId: s.costBearerId,
      grantId: s.grantId,
      pendingOneTimeGrant: s.pendingOneTimeGrant,
      setGrantId: s.setGrantId,
      setPendingOneTimeGrant: s.setPendingOneTimeGrant,
      cart: s.cart,
      startDate: s.startDate,
      endDate: s.endDate,
      startTime: s.startTime,
      endTime: s.endTime,
      useHourlyBilling: s.useHourlyBilling,
      pricing: s.pricing,
      paymentMethod: s.paymentMethod,
      depositPaid: s.depositPaid,
      notes: s.notes,
      crossSellItems: s.crossSellItems,
      selectedPackage: s.selectedPackage,
      packageClassifiedItems: s.packageClassifiedItems,
      consignmentQuantities: s.consignmentQuantities,
      accessoryPackage: s.accessoryPackage,
      accessoryQuantities: s.accessoryQuantities,
      // E-105 Phase 2: normalized 1:N packageSelections for POST /rentals payload.
      packageSelections: s.packageSelections,
      migrateAccessoryPackageToSelections: s.migrateAccessoryPackageToSelections,
      overrideExpiredId: s.overrideExpiredId,
      overrideReason: s.overrideReason,
      loyaltyRedeem: s.loyaltyRedeem,
      draftId: s.draftId,
      getPanelValidity: s.getPanelValidity,
      isSubmittable: s.isSubmittable,
    }))
  );
  const { checkout } = useRentalCheckout();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // E-105 Phase 2 feature gate. UI-only — backend separately enforces with HTTP 403
  // (RentalController.create + BERLES_MULTI_PACKAGE per-tenant flag).
  const isMultiPackageEnabled = useFeatureFlag('BERLES_MULTI_PACKAGE');

  // E-105 Sprint 20 W0 V2 layout gate (ADR-103 Amendment-1 candidate, mockup
  // ratified 2026-05-11). When ON, replaces accordion Equipment + Packages
  // panels with a single 3-oszlopos EquipmentPackageStepV2. Default OFF —
  // opt-in via localStorage `feature.rentalWizardV2=true`. Phase 1 scaffold
  // visibility gate; real wiring iterative (Phase 2-4 in subsequent PRs).
  const isV2LayoutEnabled = useFeatureFlag('RENTAL_WIZARD_V2_LAYOUT');

  // ADR-103 Amendment-4 (M1.0): single-route + step-state step-wizard a V2
  // single-page FÖLÖTT. Háromállapotú coexistence — ha ON, a StepWizardShell
  // veszi át a render-t (a panelek step-szegmensekbe csoportosulnak). Default
  // OFF mindenhol amíg az M1.1 step-screen-ek épülnek (opt-in localStorage).
  const isStepWizardEnabled = useFeatureFlag('RENTAL_STEP_WIZARD');

  // Lazy migration: if the flag flipped ON for a tenant whose current DRAFT was
  // started in singular accessoryPackage mode, migrate the legacy state into the
  // normalized packageSelections[] array. Idempotent — no-op if nothing to migrate.
  useEffect(() => {
    if (isMultiPackageEnabled) migrateAccessoryPackageToSelections();
  }, [isMultiPackageEnabled, migrateAccessoryPackageToSelections]);

  // Story 105-3: aggregate submit-képesség store-ból.
  // Story 70-1E-a: if operator opted-in to override expired ID, reason is mandatory.
  const missingOverrideReason = overrideExpiredId && overrideReason.trim().length === 0;
  const canSubmit = isSubmittable() && !missingOverrideReason;

  /**
   * Handle rental completion (Story 46-1: Payment method wizard)
   * Flow:
   * 1. CASH/TRANSFER → Create rental + confirm pickup → ACTIVE
   * 2. CARD → Create rental (DRAFT) + MyPOS SALE → Confirm pickup → ACTIVE
   */
  const handleComplete = async () => {
    // Validation
    if (!partner || cart.length === 0 || !startDate || !endDate || !paymentMethod || !pricing) {
      toast({
        title: t('rental.wizard.missingData'),
        description: t('rental.wizard.missingDataDesc'),
        variant: 'destructive',
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Build cross-sell additionalItems payload (Story Q-XSL-1)
      const additionalItems =
        crossSellItems.length > 0
          ? crossSellItems.map(item => ({
              productId: item.product.id,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
            }))
          : undefined;

      // E-105 Phase 2: forward normalized 1:N package selections. Only sent
      // when the feature flag is ON — otherwise the legacy singular K3 flow
      // owns package state via accessoryPackage / consignment endpoint.
      const packageSelectionsPayload =
        isMultiPackageEnabled && packageSelections.length > 0
          ? packageSelections.map(sel => ({
              clientSelectionId: sel.selectionId,
              equipmentId: sel.equipmentId,
              packageId: sel.packageId,
              ...(sel.qtyOverrides ? { qtyOverrides: sel.qtyOverrides } : {}),
            }))
          : undefined;

      // Create rental with deposit payment method + cross-sell products
      // Kaució (preAuth) is ONLY on rental amount, NOT on products (Q-XSL-1)
      const result = await checkout({
        partnerId: partner.id,
        costBearerId: costBearerId ?? undefined,
        items: cart.map(item => ({
          equipmentId: item.equipment.id,
          quantity: item.quantity,
        })),
        startDate: mergeDateAndTime(startDate, startTime),
        endDate: mergeDateAndTime(endDate, endTime),
        startTime: startTime ?? undefined,
        endTime: endTime ?? undefined,
        useHourlyBilling: useHourlyBilling || undefined,
        notes,
        // A NYERS PaymentMethod enum-ot adjuk át ('CASH'|'CARD'|'TRANSFER') —
        // a useDeposits.checkout UPPERCASE-t vár (useDeposits.ts:78,:267), és a
        // createRental mappel egyszer lowercase-re (SSoT, :155). NE pre-mappeld
        // itt, különben a CARD-ág sosem fut (CARD/MyPOS verify-fogás 2026-06-02).
        paymentMethod,
        depositAmount: pricing.depositTotal,
        depositPaid: depositPaid || undefined,
        additionalItems,
        // Story 70-1E-a: propagate override expired ID flag + reason to backend
        overrideExpiredId: overrideExpiredId || undefined,
        overrideReason:
          overrideExpiredId && overrideReason.trim().length > 0 ? overrideReason.trim() : undefined,
        packageSelections: packageSelectionsPayload,
        // FE-E: loyalty commit-spend a végleges rentalId-re (draftId idempotency);
        // a partner.id + draftId a reserve-ben használt kulcsokkal egyező.
        loyaltyRedeem: buildLoyaltyCommitPayload(loyaltyRedeem, partner.id, draftId),
      });

      // P0-1: Create consignment records if package has CONSIGNMENT items
      const consignmentItems = (packageClassifiedItems ?? []).filter(
        i => i.settlementType === 'CONSIGNMENT'
      );
      if (selectedPackage && consignmentItems.length > 0 && result.rental.id) {
        try {
          const { createConsignment } = await import('@/api/consignments');
          await createConsignment(result.rental.id, {
            items: consignmentItems
              .filter(item => {
                const qty = consignmentQuantities?.get(item.id) ?? item.quantity;
                return qty > 0 && item.productId != null;
              })
              .map(item => ({
                productId: item.productId!,
                quantity: consignmentQuantities?.get(item.id) ?? item.quantity,
                unitPrice: item.unitPrice ?? 0,
              })),
          });
        } catch (consignmentErr) {
          // Non-blocking: consignment creation failure shouldn't prevent rental success
          console.error('Consignment creation failed:', consignmentErr);
          toast({
            title: t('rental.consignment.createFailed'),
            description:
              consignmentErr instanceof Error ? consignmentErr.message : 'Consignment error',
            variant: 'destructive',
          });
        }
      }

      // K3: Accessory package consignment handling
      if (accessoryPackage && result.rental.id) {
        try {
          const accessoryConsignmentItems = accessoryPackage.items
            .filter(item => item.settlement === 'CONSIGNMENT')
            .filter(item => (accessoryQuantities[item.id] ?? item.quantity) > 0)
            .filter(item => item.productId != null)
            .map(item => ({
              productId: item.productId!,
              quantity: accessoryQuantities[item.id] ?? item.quantity,
              unitPrice: item.unitPrice ?? 0,
            }));

          if (accessoryConsignmentItems.length > 0) {
            const { createConsignment } = await import('@/api/consignments');
            await createConsignment(result.rental.id, { items: accessoryConsignmentItems });
          }
        } catch (accessoryErr) {
          // Non-blocking: log but don't prevent rental success
          console.error('Accessory consignment creation failed:', accessoryErr);
          toast({
            title: t('rental.consignment.createFailed'),
            description:
              accessoryErr instanceof Error ? accessoryErr.message : 'Accessory consignment error',
            variant: 'destructive',
          });
        }
      }

      // STORY-1305 D1=C grant materialization (review 2026-05-13): once the rental
      // row exists, create the ONE_TIME grant binding to its real id. Same non-
      // blocking pattern as consignment/accessory above — failure is logged + a
      // separate toast, but does not block the rental success flow.
      if (
        grantId === PENDING_ONE_TIME_SENTINEL &&
        pendingOneTimeGrant &&
        result.rental.id &&
        partner
      ) {
        try {
          const { createOneTimeGrant } = await import('@/api/auth-grants');
          const granteeData: {
            name: string;
            idCardNumber?: string;
            phone?: string;
            address?: string;
          } = { name: pendingOneTimeGrant.name };
          if (pendingOneTimeGrant.idCardNumber)
            granteeData.idCardNumber = pendingOneTimeGrant.idCardNumber;
          if (pendingOneTimeGrant.phone) granteeData.phone = pendingOneTimeGrant.phone;
          if (pendingOneTimeGrant.address) granteeData.address = pendingOneTimeGrant.address;
          const grantResponse = await createOneTimeGrant({
            grantorPartnerId: partner.id,
            granteeData,
            rentalId: result.rental.id,
            documentIds: [],
            skipDocumentUpload: true,
            gracePeriodHours: 24,
          });
          // Replace sentinel with real grant id + clear captured data. The
          // subsequent reset() will null them anyway, but keep them consistent
          // for any observer that may peek before navigation completes.
          setGrantId(grantResponse.data.id);
          setPendingOneTimeGrant(null);
        } catch (grantErr) {
          console.error('ONE_TIME grant materialization failed:', grantErr);
          toast({
            title: t('rental.checkout.grant.materializeFailed'),
            description: grantErr instanceof Error ? grantErr.message : 'Grant error',
            variant: 'destructive',
          });
        }
      }

      // Success toast
      const transactionInfo = result.deposit?.myposReference
        ? ` Tranzakció ID: ${result.deposit.myposReference}`
        : '';

      toast({
        title: t('rental.wizard.rentalCreated'),
        description: t('rental.wizard.contractCreated', {
          contractNumber: result.rental.contractNumber,
          transactionInfo,
        }),
      });

      // Navigate to rental detail page
      reset();
      navigate(`/rental/${result.rental.id}`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('rental.wizard.unknownError');

      toast({
        title: t('rental.wizard.rentalCreateFailed'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (confirm(t('rental.wizard.cancelConfirm'))) {
      reset();
      navigate('/dashboard');
    }
  };

  // ─── Header (cím + cancel — nincs stepper) ─────────────────────────────────

  const headerNode = (
    <header className="border-b shadow-sm kgc-card-bg border-gray-200 dark:border-[#2d7d8a]">
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-2 sm:px-6 lg:px-8">
        <button
          onClick={handleCancel}
          aria-label={t('common.back')}
          className="flex h-7 w-7 items-center justify-center rounded-md border hover:bg-black/10 dark:hover:bg-white/10"
          style={{ color: 'var(--text-tertiary)', borderColor: 'var(--border-secondary)' }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-sm font-bold leading-tight" style={{ color: 'var(--text-primary)' }}>
            {t('rental.wizard.title')}
          </h1>
          <p className="text-[11px] leading-tight" style={{ color: 'var(--text-tertiary)' }}>
            {t('rental.wizard.subtitle')}
          </p>
        </div>
        {/* Phase 0.2 (Sally 2026-04-14): panel-management gombok — mobil scroll fájdalom csökkentés.
            Csak akkor mutatjuk ha van legalább 1 panel nyitva (különben a "mind összecsuk" no-op). */}
        {/* Am-3 Inc-2: az accordion-management gombok V2 single-page-ben no-op → elrejtve.
            Am-4 (M1.0): V3 step-wizardban sincs accordion (step-szegmensek) → szintén rejtve. */}
        {!isV2LayoutEnabled && !isStepWizardEnabled && openPanels.size > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={collapseAllPanels}
            className="shrink-0 text-xs hidden sm:inline-flex"
            style={{ color: 'var(--text-tertiary)' }}
            title={t('rental.wizard.collapseAllHint')}
          >
            {t('rental.wizard.collapseAll')}
          </Button>
        )}
        {!isV2LayoutEnabled && !isStepWizardEnabled && !canSubmit && (
          <Button
            variant="ghost"
            size="sm"
            onClick={expandInvalidOnly}
            className="shrink-0 text-xs hidden sm:inline-flex"
            style={{ color: 'var(--text-tertiary)' }}
            title={t('rental.wizard.expandInvalidHint')}
          >
            {t('rental.wizard.expandInvalid')}
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={handleCancel}
          className="shrink-0 text-xs"
          style={{ borderColor: 'var(--border-secondary)', color: 'var(--text-secondary)' }}
        >
          {t('common.cancel')}
        </Button>
      </div>
    </header>
  );

  // ─── Footer — Story 105-4 StickySummaryFooter live pricing summary ────────

  const footerNode = (
    <StickySummaryFooter
      onSubmit={handleComplete}
      isSubmitting={isSubmitting}
      canSubmit={canSubmit}
    />
  );

  // Am-3 Inc-3: a V2 jobb-oldali rail függőleges summary-variantot kap (a 03/04-v2
  // mockup vertikális aside-ja) — ugyanaz a store-adat + submit, csak az elrendezés.
  const summaryRailNode = (
    <StickySummaryFooter
      onSubmit={handleComplete}
      isSubmitting={isSubmitting}
      canSubmit={canSubmit}
      variant="rail"
    />
  );

  // ─── Panel helper — Story 105-3 getPanelValidity-ből olvas (isValid + summary) ─

  const renderPanel = (id: PanelId, stepNumber: number, title: string, child: ReactNode) => {
    const validity = getPanelValidity(id);
    return (
      <WizardPanel
        id={id}
        namespace="rental-panel"
        title={title}
        stepNumber={stepNumber}
        isOpen={isV2LayoutEnabled || openPanels.has(id)}
        isValid={validity.isValid}
        onToggle={() => togglePanel(id)}
        // Am-3 Inc-2: V2 single-page → always-expanded statikus szekciók (nincs accordion-toggle).
        nonCollapsible={isV2LayoutEnabled}
        // a11y: a panel-cím h2 mindkét layoutban (page h1 → panel h2 → step h3) —
        // nincs h1→h3 skip sem V2 (nonCollapsible) sem V1 (accordion, heading-wrapped button) flow-ban.
        headingLevel="h2"
        {...(validity.summary ? { summary: validity.summary } : {})}
      >
        {child}
      </WizardPanel>
    );
  };

  // ─── ADR-103 Amendment-2 Inc-1: single-page main + summary-rail (aside) ──────
  // V2 flag ON → a panelek a main oszlopba, a live-summary (StickySummaryFooter)
  // jobb oldali persistent sticky rail-be (lg+); mobilon (<lg) marad a footer-slot.
  // Presentational: a wizard-store / createRental / route VÁLTOZATLAN (Am-2 D-2).
  const panelsNode = (
    // Story 105-6: ArrowUp/Down/Home/End keyboard nav accordion-header-ek között
    <div className="space-y-3" onKeyDown={handleWizardPanelKeyNav}>
      {renderPanel('partner', 1, t('table.partner'), <PartnerStep />)}
      {/* E-105 V2 layout gate (Sprint 20 W0 D3): single 3-oszlopos panel replaces
            accordion Equipment + Packages when flag ON. Phase 1 scaffold — visible
            only via localStorage opt-in. */}
      {isV2LayoutEnabled
        ? renderPanel(
            'equipment',
            2,
            t('rental.wizard.v2.combinedStepTitle'),
            <EquipmentPackageStepV2 />
          )
        : renderPanel('equipment', 2, t('rental.wizard.stepEquipmentTitle'), <EquipmentStep />)}
      {!isV2LayoutEnabled &&
        isMultiPackageEnabled &&
        renderPanel(
          'packages',
          3,
          t('rental.wizard.packages.stepTitle'),
          <PackageAndAccessoriesStep />
        )}
      {renderPanel(
        'costBearer',
        isMultiPackageEnabled ? 4 : 3,
        t('rental.wizard.stepCostBearerTitle'),
        <CostBearerStep />
      )}
      {renderPanel('schedule', isMultiPackageEnabled ? 5 : 4, t('common.details'), <DetailsStep />)}
      {renderPanel(
        'payment',
        isMultiPackageEnabled ? 6 : 5,
        t('rental.wizard.stepSummaryTitle'),
        <SummaryStep />
      )}
    </div>
  );

  // ADR-103 Am-4 (M1.0): V3 step-wizard ág — a StepWizardShell veszi át a
  // teljes render-t (saját breadcrumb + step-szegmens + rail-ownership). A V2
  // single-page + legacy accordion ág változatlan alatta.
  if (isStepWizardEnabled) {
    return (
      <WizardShell mode="custom" stepper={headerNode} footer={null}>
        <WizardSubHeader />
        <StepWizardShell
          onSubmit={handleComplete}
          isSubmitting={isSubmitting}
          canSubmit={canSubmit}
        />
      </WizardShell>
    );
  }

  return (
    <WizardShell
      mode="custom"
      stepper={headerNode}
      // V2: a summary EGYETLEN instance-ként a jobb rail-be kerül (mobilon a main alá
      // stacked, lg+ sticky), így a footer-slot V2-ben üres (nincs dupla StickySummaryFooter).
      footer={isV2LayoutEnabled ? null : footerNode}
    >
      {/* STORY-1305 AC#7 D5-A: sticky sub-header relationship line */}
      <WizardSubHeader />
      {isV2LayoutEnabled ? (
        <div
          className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-6 sm:px-6 lg:flex-row lg:px-8"
          data-testid="rental-wizard-v2-layout"
        >
          <main className="min-w-0 flex-1">{panelsNode}</main>
          <aside className="w-full shrink-0 lg:w-[380px]" data-testid="rental-wizard-summary-rail">
            <div className="lg:sticky lg:top-24">{summaryRailNode}</div>
          </aside>
        </div>
      ) : (
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">{panelsNode}</div>
      )}
    </WizardShell>
  );
}
